/**
 * Admin Users service (no server).
 *
 * Organisations are Firestore docs keyed by domain (admin-writable per
 * rules). User accounts are created through a *secondary* Firebase app
 * instance so the admin's own session is not replaced — the new user gets a
 * verification email and their profile doc is created automatically on
 * their first verified login. Disabling/deleting accounts is done in the
 * Firebase Console (Authentication tab).
 */

import { initializeApp, deleteApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, firebaseConfig, useEmulator } from '../../../../shared/firebase';
import { authErrorMessage } from '../../../../shared/services/authService';

/**
 * Create a new organization by domain (admin only).
 * @param {Object} data - { domain: string }
 * @returns {Promise<Object>} Created org { orgId, domain, ... }
 */
export const createOrganization = async (data) => {
  try {
    const domain = data.domain?.trim().toLowerCase();
    if (!domain) {
      throw new Error('domain is required');
    }
    const ref = doc(db, 'organisations', domain);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      return { orgId: domain, ...existing.data() };
    }
    const org = {
      orgId: domain,
      domain,
      name: domain,
      createdAt: new Date().toISOString(),
    };
    await setDoc(ref, org);
    return org;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

/**
 * Create a new user account (admin only in practice — anyone could register
 * themselves anyway; this exists so admins can pre-provision accounts).
 * The user must verify their email before they can sign in.
 * @param {Object} data - { email, password, name }
 * @returns {Promise<Object>}
 */
export const createUser = async (data) => {
  const secondaryApp = initializeApp(firebaseConfig, `admin-user-creation-${Date.now()}`);
  const secondaryAuth = getAuth(secondaryApp);
  if (useEmulator) {
    connectAuthEmulator(secondaryAuth, 'http://127.0.0.1:9099', { disableWarnings: true });
  }
  try {
    const credential = await createUserWithEmailAndPassword(
      secondaryAuth,
      data.email.toLowerCase().trim(),
      data.password
    );
    if (data.name) {
      await updateProfile(credential.user, { displayName: data.name });
    }
    const response = await fetch('/api/auth/firebase/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: data.email.toLowerCase().trim() }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || 'Failed to send verification email');
    }
    const created = {
      success: true,
      user: {
        userId: credential.user.uid,
        email: credential.user.email,
        name: data.name || null,
      },
      message: 'User created. They must verify their email before signing in.',
    };
    await signOut(secondaryAuth);
    return created;
  } catch (error) {
    console.error('Error creating user:', error);
    throw new Error(authErrorMessage(error, 'Failed to create user'));
  } finally {
    await deleteApp(secondaryApp).catch(() => {});
  }
};
